import React, { useMemo } from "react";

import {
  FaLayerGroup,
  FaUsers,
} from "react-icons/fa";

import { ROLE_OPTIONS } from "../../../../utils/rolesOptions";

import {
  getActorGroupLabel,
  parseIdArray,
} from "../utils/voteAdmin.utils";

const cx = (...classes) =>
  classes.filter(Boolean).join(" ");

export default function AudienceChips({
  roleIds = [],
  actorGroupIds = [],

  /*
   * Liste complète chargée avec getGroupsList().
   */
  actorGroups = [],

  /*
   * Groupes directement renvoyés par le vote :
   * vote.allowed_actor_groups
   */
  selectedActorGroups = [],
}) {
  const normalizedRoleIds = useMemo(
    () => parseIdArray(roleIds),
    [roleIds],
  );

  const normalizedActorGroupIds = useMemo(
    () => parseIdArray(actorGroupIds),
    [actorGroupIds],
  );

  const roleLabelsById = useMemo(() => {
    const map = new Map();

    ROLE_OPTIONS.forEach((role) => {
      const roleId = Number(
        role.id ??
          role.role_id ??
          role.value,
      );

      if (Number.isFinite(roleId)) {
        map.set(
          roleId,
          role.label ??
            role.nom ??
            role.name ??
            `Rôle ${roleId}`,
        );
      }
    });

    return map;
  }, []);

  const actorGroupsById = useMemo(() => {
    const map = new Map();

    /*
     * On commence par la liste générale.
     */
    actorGroups.forEach((group) => {
      const groupId = Number(group?.id);

      if (Number.isFinite(groupId)) {
        map.set(groupId, group);
      }
    });

    /*
     * Les groupes directement associés au vote sont prioritaires.
     * Cela permet d'afficher le nom même si la liste générale
     * n'est pas encore chargée.
     */
    selectedActorGroups.forEach((group) => {
      const groupId = Number(group?.id);

      if (Number.isFinite(groupId)) {
        map.set(groupId, group);
      }
    });

    return map;
  }, [actorGroups, selectedActorGroups]);

  const selectedRoles = useMemo(
    () =>
      normalizedRoleIds.map((roleId) => ({
        id: roleId,
        label:
          roleLabelsById.get(roleId) ??
          `Rôle ${roleId}`,
      })),
    [
      normalizedRoleIds,
      roleLabelsById,
    ],
  );

  const selectedGroups = useMemo(
    () =>
      normalizedActorGroupIds.map(
        (groupId) => {
          const group =
            actorGroupsById.get(groupId);

          return {
            id: groupId,
            label: group
              ? getActorGroupLabel(group)
              : `Groupe #${groupId}`,
          };
        },
      ),
    [
      normalizedActorGroupIds,
      actorGroupsById,
    ],
  );

  const hasAudience =
    selectedRoles.length > 0 ||
    selectedGroups.length > 0;

  if (!hasAudience) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
        <FaUsers />

        Tous les utilisateurs
      </span>
    );
  }

  return (
    <div className="flex max-w-[320px] flex-wrap gap-1.5">
      {selectedRoles.map((role) => (
        <span
          key={`role-${role.id}`}
          title={role.label}
          className={cx(
            "inline-flex max-w-full items-center gap-1.5",
            "rounded-full border border-indigo-200",
            "bg-indigo-50 px-2.5 py-1",
            "text-xs font-black text-indigo-700",
            "dark:border-indigo-500/30",
            "dark:bg-indigo-500/10",
            "dark:text-indigo-300",
          )}
        >
          <FaUsers className="shrink-0" />

          <span className="truncate">
            {role.label}
          </span>
        </span>
      ))}

      {selectedGroups.map((group) => (
        <span
          key={`group-${group.id}`}
          title={group.label}
          className={cx(
            "inline-flex max-w-full items-center gap-1.5",
            "rounded-full border border-violet-200",
            "bg-violet-50 px-2.5 py-1",
            "text-xs font-black text-violet-700",
            "dark:border-violet-500/30",
            "dark:bg-violet-500/10",
            "dark:text-violet-300",
          )}
        >
          <FaLayerGroup className="shrink-0" />

          <span className="truncate">
            {group.label}
          </span>
        </span>
      ))}
    </div>
  );
}